/**
 * NumericInput — Formatted number input with thousand separators + configurable decimals
 *
 * Uses react-number-format for smooth typing with auto-formatting
 *
 * Usage:
 *   <NumericInput value={vatRate} onChange={setVatRate} decimals={1} placeholder="10" />
 *   <NumericInput value={shippingFee} onChange={setShippingFee} placeholder="100.000" />
 */

import React, { useRef } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { NumericFormat } from 'react-number-format';

export function resolveNumericValueChange(
    values: { floatValue?: number },
    sourceInfo?: { source?: string },
    min?: number,
    max?: number,
): { shouldEmit: boolean; value: number | null } {
    if (sourceInfo?.source === 'prop') {
        return { shouldEmit: false, value: null };
    }
    let val = values.floatValue;
    if (val === undefined) {
        return { shouldEmit: true, value: null };
    }
    if (min !== undefined && val < min) val = min;
    if (max !== undefined && val > max) val = max;
    return { shouldEmit: true, value: val };
}

export function shouldPreserveSelectionOnMouseUp(params: {
    justFocused: boolean;
    valueLength: number;
    selectionStart: number | null | undefined;
    selectionEnd: number | null | undefined;
}): boolean {
    const { justFocused, valueLength, selectionStart, selectionEnd } = params;
    if (!justFocused || valueLength <= 0) return false;
    if (selectionStart == null || selectionEnd == null) return true;
    return (
        selectionStart === selectionEnd ||
        (selectionStart === 0 && selectionEnd === valueLength)
    );
}

export interface NumericFocusSelectionController {
    handleMouseDown: (isAlreadyFocused: boolean) => void;
    handleFocusCapture: (valueLength: number) => boolean;
    peekShouldSelectOnPointerRelease: (params: {
        valueLength: number;
        selectionStart: number | null | undefined;
        selectionEnd: number | null | undefined;
    }) => boolean;
    handleClick: (params: {
        valueLength: number;
        selectionStart: number | null | undefined;
        selectionEnd: number | null | undefined;
    }) => boolean;
    handleKeyDown: () => void;
    handleBlur: () => void;
}

export function createNumericFocusSelectionController(): NumericFocusSelectionController {
    let selectOnPointerRelease = false;
    let hasTypedSinceFocus = false;

    return {
        handleMouseDown(isAlreadyFocused: boolean) {
            selectOnPointerRelease = !isAlreadyFocused;
        },
        handleFocusCapture(valueLength: number) {
            hasTypedSinceFocus = false;
            return valueLength > 0;
        },
        peekShouldSelectOnPointerRelease({ valueLength, selectionStart, selectionEnd }) {
            return shouldPreserveSelectionOnMouseUp({
                justFocused: selectOnPointerRelease && !hasTypedSinceFocus,
                valueLength,
                selectionStart,
                selectionEnd,
            });
        },
        handleClick({ valueLength, selectionStart, selectionEnd }) {
            const shouldSelect = shouldPreserveSelectionOnMouseUp({
                justFocused: selectOnPointerRelease && !hasTypedSinceFocus,
                valueLength,
                selectionStart,
                selectionEnd,
            });
            selectOnPointerRelease = false;
            return shouldSelect;
        },
        handleKeyDown() {
            hasTypedSinceFocus = true;
            selectOnPointerRelease = false;
        },
        handleBlur() {
            selectOnPointerRelease = false;
            hasTypedSinceFocus = false;
        },
    };
}

interface NumericInputProps {
    value: number | string | undefined | null;
    onChange: (value: any) => void;
    onBlur?: () => void;
    /** Number of decimal places (default: 0) */
    decimals?: number;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    suffix?: string;
    id?: string;
    name?: string;
    min?: number;
    max?: number;
    [key: string]: any;
}

export function NumericInput({
    value,
    onChange,
    decimals = 0,
    placeholder,
    className,
    disabled,
    suffix,
    id,
    name,
    min,
    max,
    onBlur,
    ...rest
}: NumericInputProps) {
    const selectionCtrlRef = useRef<NumericFocusSelectionController | null>(null);
    if (!selectionCtrlRef.current) {
        selectionCtrlRef.current = createNumericFocusSelectionController();
    }

    return (
        <div className="relative">
            <NumericFormat
                id={id}
                name={name}
                value={value ?? ''}
                inputMode={rest.inputMode ?? (decimals > 0 ? 'decimal' : 'numeric')}
                {...rest}
                onValueChange={(values, sourceInfo) => {
                    const resolved = resolveNumericValueChange(values, sourceInfo, min, max);
                    if (!resolved.shouldEmit) return;
                    onChange(resolved.value);
                }}
                onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => {
                    const target = e.currentTarget;
                    const isAlreadyFocused =
                        typeof document !== 'undefined' && document.activeElement === target;
                    selectionCtrlRef.current?.handleMouseDown(isAlreadyFocused);
                    rest.onMouseDown?.(e);
                }}
                onFocusCapture={(e: React.FocusEvent<HTMLInputElement>) => {
                    // Đồng bộ tức thời (không bị trễ bởi setTimeout(0) bên trong react-number-format)
                    const target = e.currentTarget;
                    if (selectionCtrlRef.current?.handleFocusCapture((target.value || '').length)) {
                        target.select();
                    }
                    rest.onFocusCapture?.(e);
                }}
                onFocus={(e) => {
                    rest.onFocus?.(e);
                }}
                onMouseUp={(e) => {
                    const target = (e.currentTarget ?? e.target) as HTMLInputElement;
                    if (
                        selectionCtrlRef.current?.peekShouldSelectOnPointerRelease({
                            valueLength: (target.value || '').length,
                            selectionStart: target.selectionStart,
                            selectionEnd: target.selectionEnd,
                        })
                    ) {
                        target.select();
                    }
                    rest.onMouseUp?.(e);
                }}
                onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                    // Trong Chromium/Blink, mouseup default action đặt lại con trỏ sau onMouseUp;
                    // thực hiện select() tại onClick (ngay sau mouseup) bảo toàn bôi đen toàn bộ 100% khi click lần đầu.
                    const target = e.currentTarget;
                    if (
                        selectionCtrlRef.current?.handleClick({
                            valueLength: (target.value || '').length,
                            selectionStart: target.selectionStart,
                            selectionEnd: target.selectionEnd,
                        })
                    ) {
                        target.select();
                    }
                    rest.onClick?.(e);
                }}
                onKeyDown={(e) => {
                    selectionCtrlRef.current?.handleKeyDown();
                    rest.onKeyDown?.(e);
                }}
                onBlur={() => {
                    selectionCtrlRef.current?.handleBlur();
                    onBlur?.();
                }}
                decimalScale={decimals > 0 ? decimals : 0}
                thousandSeparator="."
                decimalSeparator=","
                // Ô có số lẻ: gõ "." hay "," đều là dấu thập phân (67.5 → 67,5), không bị hiểu là phân cách nghìn (FB-U0ZVW8)
                allowedDecimalSeparators={decimals > 0 ? [",", "."] : undefined}
                placeholder={placeholder}
                disabled={disabled}
                className={cn(
                    'text-right tabular-nums font-mono placeholder:text-slate-300 placeholder:font-normal',
                    suffix && 'pr-8',
                    className,
                )}
                customInput={Input}
                allowNegative={min === undefined || min < 0}
            />
            {suffix && (
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
                    {suffix}
                </span>
            )}
        </div>
    );
}
